/**
 * PostMessage API for external communication with Storyline
 * Handles bidirectional communication for flow synchronization and variable management
 */

import React from 'react';

import { logger } from '../utils/logger';

// Create component logger for postMessage service
const postMessageLogger = logger.withComponent('PostMessageService');

export interface PostMessageOptions {
  targetOrigin?: string; // Default: '*'
  enableLogging?: boolean; // Enable debug logging, default: false
}

export interface FlowInitMessage {
  type: 'flow:init';
  payload: {
    flowId: string;
    variables?: Record<string, any>;
  };
}

export interface FlowStartMessage {
  type: 'flow:start';
  payload: {
    flowId: string;
  };
}

export interface FlowStopMessage {
  type: 'flow:stop';
  payload: {
    flowId: string;
  };
}

export interface VariableUpdateMessage {
  type: 'variable:update';
  payload: {
    key: string;
    value: any;
  };
}

export interface VariableSyncMessage {
  type: 'variable:sync';
  payload: {
    variables: Record<string, any>;
  };
}

export interface FlowStateMessage {
  type: 'flow:state';
  payload: {
    flowId: string;
    stepId: string;
    isActive: boolean;
    variables: Record<string, any>;
  };
}

export interface ErrorMessage {
  type: 'error';
  payload: {
    error: string;
    details?: any;
  };
}

export type IncomingMessage = 
  | FlowInitMessage 
  | FlowStartMessage 
  | FlowStopMessage 
  | VariableUpdateMessage 
  | VariableSyncMessage;

export type OutgoingMessage = 
  | FlowStateMessage 
  | VariableUpdateMessage 
  | VariableSyncMessage 
  | ErrorMessage;

export type MessageHandler<T = any> = (payload: T) => void | Promise<void>;

export class PostMessageService {
  private handlers: Map<string, MessageHandler[]> = new Map();
  private options: PostMessageOptions;
  private isListening = false;

  constructor(options: PostMessageOptions = {}) {
    this.options = {
      targetOrigin: '*',
      enableLogging: false,
      ...options,
    };
  }

  /**
   * Start listening for incoming messages
   */
  startListening(): void {
    if (this.isListening) return;

    window.addEventListener('message', this.handleIncomingMessage);
    this.isListening = true;
    
    if (this.options.enableLogging) {
      postMessageLogger.info('[PostMessageService] Started listening for messages');
    }
  }

  /**
   * Stop listening for incoming messages
   */
  stopListening(): void {
    if (!this.isListening) return;

    window.removeEventListener('message', this.handleIncomingMessage);
    this.isListening = false;
    
    if (this.options.enableLogging) {
      postMessageLogger.info('[PostMessageService] Stopped listening for messages');
    }
  }

  /**
   * Handle incoming PostMessage events
   */
  private handleIncomingMessage = (event: MessageEvent) => {
    try {
      // Basic security check - ensure message has required structure
      if (!event.data || typeof event.data !== 'object' || !event.data.type) {
        return; // Ignore malformed messages
      }

      const message = event.data as IncomingMessage;
      
      if (this.options.enableLogging) {
        postMessageLogger.info('[PostMessageService] Received message:', {
          type: message.type,
          payload: message.payload,
          origin: event.origin,
        });
      }

      // Dispatch to registered handlers
      const handlers = this.handlers.get(message.type) || [];
      handlers.forEach(handler => {
        try {
          handler(message.payload);
        } catch (error) {
          postMessageLogger.error('[PostMessageService] Handler error:', error);
          this.sendError(`Handler error for ${message.type}: ${error}`);
        }
      });

    } catch (error) {
      postMessageLogger.error('[PostMessageService] Message parsing error:', error);
      this.sendError(`Message parsing error: ${error}`);
    }
  };

  /**
   * Register a handler for a specific message type
   * @param type - Message type to handle
   * @param handler - Handler function
   * @returns Cleanup function to remove the handler
   */
  on<T = any>(type: string, handler: MessageHandler<T>): () => void {
    if (!this.handlers.has(type)) {
      this.handlers.set(type, []);
    }
    
    this.handlers.get(type)!.push(handler);
    
    if (this.options.enableLogging) {
      postMessageLogger.info(`[PostMessageService] Registered handler for: ${type}`);
    }

    // Return cleanup function
    return () => {
      const handlers = this.handlers.get(type);
      if (handlers) {
        const index = handlers.indexOf(handler);
        if (index > -1) {
          handlers.splice(index, 1);
        }
      }
    };
  }

  /**
   * Send a message to the parent window
   * @param message - Message to send
   */
  send(message: OutgoingMessage): void {
    try {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage(message, this.options.targetOrigin!);
        
        if (this.options.enableLogging) {
          postMessageLogger.info('[PostMessageService] Sent message:', {
            type: message.type,
            payload: message.payload,
            targetOrigin: this.options.targetOrigin,
          });
        }
      }
    } catch (error) {
      postMessageLogger.error('[PostMessageService] Send error:', error);
    }
  }

  /**
   * Send flow state update
   */
  sendFlowState(flowId: string, stepId: string, isActive: boolean, variables: Record<string, any>): void {
    this.send({
      type: 'flow:state',
      payload: { flowId, stepId, isActive, variables },
    });
  }

  /**
   * Send variable update
   */
  sendVariableUpdate(key: string, value: any): void {
    this.send({
      type: 'variable:update',
      payload: { key, value },
    });
  }

  /**
   * Send variables sync
   */
  sendVariableSync(variables: Record<string, any>): void {
    this.send({
      type: 'variable:sync',
      payload: { variables },
    });
  }

  /**
   * Send error message
   */
  sendError(error: string, details?: any): void {
    this.send({
      type: 'error',
      payload: { error, details },
    });
  }

  /**
   * Check if we're running in an iframe (embedded mode)
   */
  isEmbedded(): boolean {
    return window.parent !== window;
  }

  /**
   * Get current options
   */
  getOptions(): PostMessageOptions {
    return { ...this.options };
  }

  /**
   * Update options
   */
  updateOptions(newOptions: Partial<PostMessageOptions>): void {
    this.options = { ...this.options, ...newOptions };
  }

  /**
   * Remove all handlers and stop listening
   */
  destroy(): void {
    this.stopListening();
    this.handlers.clear();
    
    if (this.options.enableLogging) {
      postMessageLogger.info('[PostMessageService] Service destroyed');
    }
  }
}

// Export singleton instance with logging enabled in development
export const postMessageService = new PostMessageService({
  enableLogging: process.env.NODE_ENV === 'development'
});

/**
 * React hook for using PostMessage service in components
 */
export const usePostMessage = (options?: PostMessageOptions) => {
  const [service] = React.useState(() => new PostMessageService(options));

  React.useEffect(() => {
    service.startListening();
    return () => service.destroy();
  }, [service]);

  return service;
};